/**
 * The background colours of both modes (merged page, GĐ2): the catalogue's colours (catalog.json "backgrounds") as
 * radio swatches, then "Màu…" for any colour. "Ảnh thẻ" marks the colours right for the spec (✓); "Ảnh chuyên nghiệp"
 * puts the person on the chosen colour instead of a library backdrop.
 */
import { useId } from "react";

export type Rgb = [number, number, number];

export interface Swatch {
  key: string;
  label: string;
  /** null: no colour of its own ("Giữ nguyên", or "Tuỳ chọn" = the picker's colour) */
  rgb: Rgb | null;
}

export const toHex = (rgb: Rgb) => `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
export function fromHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

interface Props {
  legend: string;
  swatches: Swatch[];
  /** the chosen swatch's key, null = none */
  value: string | null;
  /** the picker's colour (shown on the "custom" swatch) */
  custom: Rgb;
  onSelect: (key: string, rgb: Rgb | null) => void;
  /** "Màu…" changed the colour (also selects "custom") */
  onCustom: (rgb: Rgb) => void;
  /** a ✓ after the label (the spec's right colours) */
  marked?: (key: string) => boolean;
  hint?: string;
}

export function ColourSwatches({ legend, swatches, value, custom, onSelect, onCustom, marked, hint }: Props) {
  const name = useId();
  const pickerId = useId();
  const hasCustom = swatches.some((s) => s.key === "custom");
  return (
    <fieldset className="field">
      <legend>{legend}</legend>
      <div className="swatches">
        {swatches.map((s) => {
          const rgb = s.key === "custom" ? custom : s.rgb;
          return (
            <label key={s.key} className={`swatch${value === s.key ? " is-selected" : ""}`}>
              <input type="radio" name={name} value={s.key} checked={value === s.key}
                     onChange={() => onSelect(s.key, s.key === "custom" ? custom : s.rgb)} />
              <span className="swatch-color" aria-hidden="true"
                    style={rgb ? { background: `rgb(${rgb.join(",")})` } : undefined} data-keep={!rgb || undefined} />
              <span>{s.label}{marked?.(s.key) ? " ✓" : ""}</span>
            </label>
          );
        })}
        {hasCustom && (
          <label className="swatch swatch-picker" htmlFor={pickerId}>
            <input id={pickerId} type="color" value={toHex(custom)} aria-label="Chọn màu nền tuỳ chọn"
                   onChange={(e) => {
                     const rgb = fromHex(e.target.value);
                     if (rgb) onCustom(rgb);
                   }} />
            <span aria-hidden="true">Màu…</span>
          </label>
        )}
      </div>
      {hint && <small className="hint">{hint}</small>}
    </fieldset>
  );
}
